import QRCode from 'qrcode';
import logoUrl from '../assets/ross-qr-logo.jpg';

// A coupon's QR code: a Telegram deep link that opens the chosen pool bot and sends
// "/start <code>", which proxy-channels and the backend unwrap and redeem like the bare code.
//
// Drawn the way the BAIFF invitation codes were (ross-coupon-landing/tools/make_qr.py), so
// the two look like one family: error correction H, 14 px modules, a 4-module quiet zone,
// and the Ross mark on a white rounded plate 22% of the width across. H recovers about 30%
// of the code, which is what lets the mark sit over the modules; make_qr.py read 5,000 of
// these back with ZXing at 100, 50 and 30% scale before the recipe was trusted. Change a
// number here only after reading codes back again: a code with a logo punched into it can
// stop decoding without looking any different.
//
// Generated in the browser and never stored: the image is a function of the bot and the
// code, so there is nothing worth keeping.
export const QR_STYLE = Object.freeze({
  errorCorrectionLevel: 'H',
  boxSize: 14,
  border: 4,
  logoRatio: 0.22,
  logoPadRatio: 0.1,
});

// Telegram's rule for the start parameter: 1 to 64 of A-Z, a-z, 0-9, _ and -. A coupon
// code may be up to 100 characters, and the backend's isalnum() admits accented letters,
// so not every code can travel in a link.
const START_PAYLOAD = /^[A-Za-z0-9_-]{1,64}$/;
export const START_PAYLOAD_MAX = 64;

/** Why this code cannot be put in a Telegram link, or null when it can. */
export function deepLinkProblem(code) {
  const c = String(code ?? '');
  if (!c) return 'This coupon has no code.';
  if (c.length > START_PAYLOAD_MAX) {
    return `A Telegram link carries at most ${START_PAYLOAD_MAX} characters, and this code has ${c.length}.`;
  }
  if (!START_PAYLOAD.test(c)) {
    return 'A Telegram link carries only the letters A to Z, digits, - and _, and this code has other characters.';
  }
  return null;
}

export function telegramDeepLink(botUsername, code) {
  const bot = String(botUsername || '').replace(/^@/, '');
  return `https://t.me/${bot}?start=${code}`;
}

export function qrFileName(code, botUsername) {
  const safe = (s) => String(s || '').replace(/[^A-Za-z0-9_-]+/g, '-');
  return `coupon-qr-${safe(code)}-${safe(botUsername)}.png`;
}

/**
 * Where the mark goes on a QR `width` pixels across: make_qr.py's paste_logo, number for
 * number (PIL's int() and // are floors, and the mark is square).
 */
export function logoLayout(width, style = QR_STYLE) {
  const size = Math.floor(width * style.logoRatio);
  const pad = Math.floor(size * style.logoPadRatio);
  const box = size + pad * 2;
  const at = Math.floor((width - box) / 2);
  return {
    size, pad, box, x: at, y: at,
    logoRadius: Math.max(3, Math.floor(size / 7)),
    plateRadius: Math.max(4, Math.floor(box / 8)),
  };
}

function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

let logoPromise = null;
function loadLogo() {
  if (!logoPromise) {
    logoPromise = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => {
        logoPromise = null;           // let the next attempt try again
        reject(new Error('The Ross mark did not load'));
      };
      img.src = logoUrl;
    });
  }
  return logoPromise;
}

/** The QR for `url` as a PNG data URL. */
export async function renderCouponQr(url, style = QR_STYLE) {
  const canvas = document.createElement('canvas');
  await QRCode.toCanvas(canvas, url, {
    errorCorrectionLevel: style.errorCorrectionLevel,
    margin: style.border,
    scale: style.boxSize,
    color: { dark: '#000000', light: '#ffffff' },
  });
  const logo = await loadLogo();
  const ctx = canvas.getContext('2d');
  const at = logoLayout(canvas.width, style);
  // The opaque white plate is not decoration: the mark is near-black, and without a light
  // border it merges into the dark modules around it and costs the decoder its edges.
  ctx.fillStyle = '#ffffff';
  roundedRect(ctx, at.x, at.y, at.box, at.box, at.plateRadius);
  ctx.fill();
  ctx.save();
  roundedRect(ctx, at.x + at.pad, at.y + at.pad, at.size, at.size, at.logoRadius);
  ctx.clip();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(logo, at.x + at.pad, at.y + at.pad, at.size, at.size);
  ctx.restore();
  return canvas.toDataURL('image/png');
}

/** The bot a new QR should default to: the fewest users, the oldest on a tie. The list
 * arrives oldest first, and the sort is stable. */
export function leastLoadedBot(bots) {
  const list = Array.isArray(bots) ? bots : [];
  return [...list].sort((a, b) => (a.user_count ?? 0) - (b.user_count ?? 0))[0] || null;
}
